<template>
  <div>
    <!-- Filters -->
    <b-card>
      <b-card-title>
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div class="financial-step-title m-1">
            <div class="m-1">
              <h4>Poupar eficiente</h4>
              <span class="text-muted h5">Passo 01</span>
            </div>
          </div>
          <b-button
            size="sm"
            variant="outline-primary"
            class="m-1"
            :disabled="generatingSuggestions || individualLoading > 0"
            @click="generateAllSuggestions"
          >
            <b-spinner
              v-if="generatingSuggestions"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="ZapIcon"
              class="mr-50"
            />
            {{ generatingSuggestions ? "Gerando todas as sugestões..." : "Gerar todas as sugestões" }}
          </b-button>
        </div>
      </b-card-title>
      <template v-if="loading">
        <b-row class="justify-content-md-center mt-2">
          <b-spinner big variant="primary" />
        </b-row>
      </template>
      <b-card-body v-else>
        <b-row>
          <b-col cols="12" md="6">
            Capacidade de poupança do cliente
            <br />
            <small class="text-muted">
              Insira o valor que você consegue poupar mensalmente. Este valor
              será usado para calcular o percentual de sua renda bruta que pode
              ser economizado e ajustado conforme as metas
            </small>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-input
              :key="'saving-' + moneyKey"
              v-money="moneyFormat"
              v-model="maxSavingAmount"
              :class="{ 'is-invalid': v$.maxSavingAmount.$error }"
              @blur="saveData"
            />
            <div class="invalid-feedback">
              <span v-if="v$.maxSavingAmount.required.$invalid">
                Você deve informar um valor válido
              </span>
            </div>
            <div v-if="percentageOfIncome" class="gross-value-info">
              <span class="pt-2">O valor corresponde a:</span> <br />
              <span class="gross-value-percentage"
                >{{ percentageOfIncome }}% da renda bruta do cliente</span
              >
            </div>
            <copilot-suggestion
              ref="copilotSavingCapacity"
              :customer-token="customerToken"
              field-key="saving_capacity"
              @manual-generate="individualLoading++"
              @manual-generate-done="individualLoading--"
              @apply="applySavingCapacity"
            />
          </b-col>
        </b-row>
        <b-row class="pt-3">
          <b-col cols="12" md="6">
            Oportunidade de corte de gastos
            <br />
            <small class="text-muted">
              Preencha este campo com despesas que podem ser reduzidas ou
              eliminadas. O sistema irá sugerir formas de otimizar seus gastos
              com base nas informações fornecidas.
            </small>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-textarea
              v-model="cutExpensesOportunities"
              @blur="saveData"
            />
            <copilot-suggestion
              ref="copilotCutExpenses"
              :customer-token="customerToken"
              field-key="cut_expenses_oportunities"
              @manual-generate="individualLoading++"
              @manual-generate-done="individualLoading--"
              @apply="applyCutExpenses"
            />
          </b-col>
        </b-row>
        <b-row class="pt-3">
          <b-col cols="12" md="6">
            Oportunidade de aumento de receitas
            <br />
            <small class="text-muted">
              Preencha com as possíveis sugestões de aumento de receitas para o
              cliente, como novas fontes de renda ou estratégias de
              investimento. Esse campo ajudará a projetar o impacto de um
              aumento de ganhos no planejamento financeiro.
            </small>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-textarea v-model="revenueOportunities" @blur="saveData" />
            <copilot-suggestion
              ref="copilotRevenue"
              :customer-token="customerToken"
              field-key="revenue_oportunities"
              @manual-generate="individualLoading++"
              @manual-generate-done="individualLoading--"
              @apply="applyRevenue"
            />
          </b-col>
        </b-row>
        <div
          class="d-flex justify-content-end align-items-end financial-next-step"
        >
          <b-button variant="primary" @click="toStep('next')">
            Próximo
          </b-button>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import CopilotSuggestion from "../components/CopilotSuggestion.vue";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { floatToMoney, moneyToFloat, suggestionToFloat } from "@/helpers/converters";
import { VMoney } from "v-money";
import { getBRLFormat } from "@/helpers/formatting";
import * as types from "../store/types";
import useAppConfig from "@core/app-config/useAppConfig";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    vSelect,
    CopilotSuggestion,
  },
  props: {
    toStep: Function,
    customerToken: String,
  },
  directives: {
    money: VMoney,
  },
  setup() {
    const { skin } = useAppConfig();
    return { toast: useToast(), skin, v$: useVuelidate() };
  },
  data() {
    return {
      loading: false,
      generatingSuggestions: false,
      individualLoading: 0,
      moneyKey: 0,
      maxSavingAmount: undefined,
      cutExpensesOportunities: undefined,
      revenueOportunities: undefined,
      cashFlowIncome: undefined,
    };
  },
  validations() {
    return {
      maxSavingAmount: { required },
    };
  },
  computed: {
    moneyFormat: function () {
      return getBRLFormat();
    },
    percentageOfIncome: function () {
      const savingAmount = moneyToFloat(this.maxSavingAmount);
      if (this.cashFlowIncome && this.cashFlowIncome > 0 && savingAmount > 0) {
        return ((savingAmount / this.cashFlowIncome) * 100).toFixed(0);
      }
      return 0;
    },
  },
  mounted() {
    this.loading = true;
    this.getEfficiency(this.$route.params.id)
      .then((response) => {
        if (response.data) {
          this.maxSavingAmount = floatToMoney(response.data.max_saving_amount);
          this.cutExpensesOportunities =
            response.data.cut_expenses_oportunities;
          this.revenueOportunities = response.data.revenue_oportunities;
        }
      })
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao obter as informações de eficiência. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading = false;
      });
    this.getCashFlowIncome(this.$route.params.id)
      .then((response) => {
        if (response.data) {
          this.cashFlowIncome = response.data.value;
        }
      })
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao obter as informações de eficiência. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  },
  methods: {
    ...mapActions({
      getEfficiency: types.GET_EFFICIENCY,
      saveEfficiency: types.SAVE_EFFICIENCY,
      getCashFlowIncome: types.GET_CASH_FLOW_INCOME,
    }),
    generateAllSuggestions() {
      const suggestions = [
        this.$refs.copilotSavingCapacity,
        this.$refs.copilotCutExpenses,
        this.$refs.copilotRevenue,
      ].filter(Boolean);
      if (!suggestions.length) return;
      this.generatingSuggestions = true;
      Promise.all(suggestions.map((s) => s.generate())).finally(() => {
        this.generatingSuggestions = false;
      });
    },
    applySavingCapacity(value) {
      this.maxSavingAmount = floatToMoney(suggestionToFloat(value));
      this.moneyKey++;
      this.saveData();
    },
    applyCutExpenses(text) {
      this.cutExpensesOportunities = text;
      this.saveData();
    },
    applyRevenue(text) {
      this.revenueOportunities = text;
      this.saveData();
    },
    saveData() {
      const { maxSavingAmount, cutExpensesOportunities, revenueOportunities } =
        this;
      const payload = {
        max_saving_amount: moneyToFloat(maxSavingAmount),
        cut_expenses_oportunities: cutExpensesOportunities,
        revenue_oportunities: revenueOportunities,
        customer_id: this.$route.params.id,
      };
      this.saveEfficiency(payload).catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao salvar as informações de eficiência. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.gross-value-info {
  margin-top: 10px;
  .gross-value-percentage {
    color: #ff9d00;
    font-weight: 600;
  }
}
textarea {
  height: 100px;
}
</style>
